// Sentry config needs to be imported first
import "src/config/sentry";
import "core-js";
import "src/i18n/init";
import fetchIntercept from "fetch-intercept";
import "@webcomponents/shadydom";
import * as Sentry from "@sentry/react";
import "./init";

import React from "react";
// import { createRoot } from "react-dom/client";
import ReactDOM from "react-dom";
import { ChakraProvider } from "@chakra-ui/react";
import theme from "src/styles/theme";
import { SarahProvider } from "@unifize/sarah";

import * as R from "ramda";

import firebase from "firebase/app";
import { PersistGate } from "redux-persist/integration/react";

import { Provider } from "react-redux";

import configureStore from "./configureStore";
import App from "src/components/App";
import ErrorBoundary from "src/components/ErrorBoundary";

import firebaseConfig from "src/config/firebase";
import { getCookie, shouldAddQueryParam } from "src/utils";
import httpMethods from "./constants/httpMethods";

import { isMobile } from "react-device-detect";
import { redirectToPwa } from "src/utils/pwa";

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}

if (isMobile) {
  redirectToPwa();
}

const { store, persistor } = configureStore();

export { store };

// Interceptor for fetch API
fetchIntercept.register({
  request: async (url, config = {}) => {
    let newConfig = config;
    let newUrl = url;

    if (newUrl.startsWith(process.env.BACKEND_URL)) {
      newConfig = R.mergeDeepRight(newConfig, {
        credentials: "include",
      });
    }

    const orgInSession = sessionStorage.getItem("lastOrg");

    if (orgInSession) {
      const tenantId = localStorage.getItem("tenantId");
      newConfig = R.mergeDeepRight(newConfig, {
        headers: {
          "x-unifize-org": orgInSession,
          ...(tenantId ? { "x-unifize-tenant": tenantId } : {}),
        },
      });

      if (newUrl.startsWith(process.env.BACKEND_URL)) {
        newUrl = new URL(newUrl);
        const params = newUrl.searchParams;

        // Don't add orgId query param for certain endpoints
        if (shouldAddQueryParam(url)) {
          params.set("orgId", orgInSession);
        }

        newUrl.search = params.toString();
        newUrl = newUrl.toString();
      }
    }

    if (
      newUrl.startsWith(process.env.BACKEND_URL) &&
      [
        httpMethods.put,
        httpMethods.patch,
        httpMethods.post,
        httpMethods.delete,
      ].includes(config.method)
    ) {
      const csrfToken = getCookie("unifize-csrf-token");
      if (csrfToken) {
        newConfig = R.mergeDeepRight(newConfig, {
          headers: { "x-unifize-csrf-token": csrfToken },
        });
      }
    }

    return [newUrl, newConfig];
  },
  requestError: (error) => {
    // Called when an error occured during another 'request' interceptor call
    return Promise.reject(error);
  },
  response: (response) => {
    switch (response.status) {
      case 400:
      case 401:
      case 403:
      case 404:
      case 408:
      case 409:
      case 429:
      case 500:
        return Promise.reject(response);
      default:
        return response;
    }
  },
  responseError: (error) => {
    // Handle an fetch error
    return Promise.reject(error);
  },
});

const originalConsoleError = console.error;

console.error = (...args) => {
  Sentry.logger.error(args[0], {
    error_code: "SENTRY_TEST",
    debug_info: { ...(args[1] || {}) },
  });

  originalConsoleError.apply(console, args);
};

const render = (Component) => {
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={store}>
      <ErrorBoundary>
        <PersistGate loading={null} persistor={persistor}>
          <ChakraProvider theme={theme} resetCSS={false}>
            <SarahProvider>
              <Component />
            </SarahProvider>
          </ChakraProvider>
        </PersistGate>
      </ErrorBoundary>
    </Provider>,
    document.getElementById("root"),
  );
};

render(App);
